import { type NodeProps } from '@xyflow/react';
import { StyleSheet } from 'aphrodite';
import { type BaseNodeData } from '../types';
import { BaseNode } from './BaseNode';
import { destructive, errorLight } from '../../../styles/colors';

/**
 * ErrorNode displays error states in the automation flow.
 * Used when child templates fail to load or have no available version.
 */
export function ErrorNode({ data, selected }: NodeProps) {
	const nodeData = data as BaseNodeData;
	const errorType = (nodeData.metadata?.error as string) || 'unknown_error';
	const templateId = nodeData.metadata?.templateId as string | undefined;

	const errorMessage =
		errorType === 'template_fetch_failed'
			? 'Failed to load template'
			: errorType === 'no_version_available'
				? 'No published or draft version'
				: 'Template error';

	return (
		<div role='alert' aria-label={`Error: ${nodeData.label}`}>
			<BaseNode
				data={{
					...nodeData,
					label: nodeData.label,
					summary: errorMessage + (templateId ? ` (${templateId})` : ''),
				}}
				selected={Boolean(selected)}
				containerStyle={errorStyles.errorContainer}
				showTargetHandle={true}
				showSourceHandle={true}
				icon={<ErrorIcon />}
			/>
		</div>
	);
}

function ErrorIcon() {
	return (
		<svg width='20' height='20' viewBox='0 0 20 20' fill='none' aria-hidden='true'>
			<circle cx='10' cy='10' r='8' stroke={destructive} strokeWidth='1.5' fill={errorLight} />
			<path d='M10 6v5' stroke={destructive} strokeWidth='2' strokeLinecap='round' />
			<circle cx='10' cy='14' r='1' fill={destructive} />
		</svg>
	);
}

const errorStyles = StyleSheet.create({
	errorContainer: {
		border: `2px solid ${destructive}`,
		backgroundColor: errorLight,
	},
});
